'use client';
import { ReactNode, useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';

const TIPS = {
  composite: 'Weighted-average composite score across the book. Higher is better. ≥4 very high, 3-4 high, 2-3 moderate, 1-2 low, <1 very low.',
  expectedReturn: 'E[r] = portfolio expected forward 12-month return. Each holding\'s composite is mapped to the empirical 8-year score-return curve, then weight-averaged.',
  stdev: 'σ (sigma) = annualized portfolio volatility. Computed as √(w\'Σw) from the real 252-day daily-return covariance matrix — accounts for diversification, not just per-name vol.',
  sharpe: 'Sharpe (gross) = E[r] / σ. Risk-adjusted return before fees. >1 good, >2 excellent.',
  beta: 'β SPY = weighted-average beta to the S&P 500 ETF. 1.0 = market-like. >1.0 = more cyclical (rises and falls more than SPY). <1.0 = more defensive.',
  netSharpe: 'Net Sharpe = (E[r] − fees) / σ. After your 1% management fee + 10% performance fee from a 0% hurdle. The honest after-fee number.',
  whatIs: 'A what-if tool. Drag any slider (cash + each holding) to see how the trade would change your portfolio\'s expected return, σ, Sharpe, β, and composite — in real time. Stats above show the simulated portfolio with a Δ-vs-current label so you see exactly what each move costs or gains. Read-only — does not save to your actual book.',
  reset: 'Reset all sliders back to your current portfolio weights from portfolio.json.',
  equalWeight: 'Set every holding to the same weight (1/N each). Useful baseline: shows what your portfolio would do without any conviction.',
  collapse: 'Hide the per-holding sliders. The stats panel + cash slider stay visible.',
  cashSlider: 'Cash bucket — the percentage of NAV held in cash. Cash earns 0 return with 0 volatility, so increasing cash drops both E[r] and σ proportionally. Sharpe is unchanged by cash.',
  rowSymbol: 'Click the symbol to drag the slider next to it. Each slider sets that name\'s raw weight; final portfolio weight = (raw / total) × (1 − cash).',
  rowSlider: 'Drag or type a percentage. The slider goes 0-50% (full range available via the number input).',
  rowCurrent: 'Your current weight on this name from portfolio.json — the baseline you are comparing the sim against.',
  rowDelta: 'Difference (in percentage points) between your simulated weight and current weight. Positive = added exposure, negative = trimmed.',
};

function Tip({ text, children }: { text: string; children: ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block">
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow((s) => !s)}
        className="cursor-help decoration-dotted decoration-[#475569] underline-offset-4 underline"
      >
        {children}
      </span>
      {show && (
        <span
          className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-2 w-64 px-3 py-2 rounded-md border border-[#334155] bg-[#0a0e14] text-[11px] leading-snug text-[#cbd5e1] normal-case tracking-normal shadow-xl pointer-events-none"
          role="tooltip"
        >
          {text}
        </span>
      )}
    </span>
  );
}

interface PerHolding {
  symbol: string;
  sector: string;
  composite: number;
  currentWeight: number;
  mu: number;
  sigmaAnnual: number;
  betaSpy: number | null;
}

interface CovMatrix { symbols: string[]; matrix: number[][] }

interface SnapshotData {
  cash: { weight: number; earns: number };
  fees: { mgmtFee: number; perfFee: number; hurdle: number };
  rebalance: { perHolding: PerHolding[]; covMatrix: CovMatrix };
  current: {
    expectedReturn: number;
    expectedStdev: number;
    expectedSharpe: number;
    composite: number;
  };
}

const fmtPct = (v: number, digits = 1) => `${(v * 100).toFixed(digits)}%`;
const fmtPp = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}pp`;
const fmtSigned = (v: number, d = 2) => `${v >= 0 ? '+' : ''}${v.toFixed(d)}`;

import { computePortfolioStats } from '@/lib/research/portfolio-stats';

export function RebalanceSimulator({ dataUrl = '/api/portfolio-snapshot' }: { dataUrl?: string } = {}) {
  const { data } = useResource<SnapshotData>(dataUrl);
  // Initial weights derived from snapshot — cash slider is independent so
  // we renormalize the per-stock weights to sum to 1 inside the stock bucket.
  const initFromData = useMemo(() => {
    if (!data) return null;
    const init: Record<string, number> = {};
    for (const h of data.rebalance?.perHolding ?? []) init[h.symbol] = h.currentWeight;
    const sum = Object.values(init).reduce((s, x) => s + x, 0);
    if (sum > 0) for (const k of Object.keys(init)) init[k] = init[k] / sum;
    return { weights: init, cashWeight: data.cash?.weight ?? 0 };
  }, [data]);
  // null = "follow snapshot"; once user edits, becomes their override.
  const [userWeights, setUserWeights] = useState<Record<string, number> | null>(null);
  const [userCashWeight, setUserCashWeight] = useState<number | null>(null);
  // useMemo so the {} fallback doesn't create a new object every render —
  // downstream useMemo (line 118) depends on `weights` identity.
  const weights = useMemo(
    () => userWeights ?? initFromData?.weights ?? {},
    [userWeights, initFromData],
  );
  const cashWeight = userCashWeight ?? initFromData?.cashWeight ?? 0;
  const setWeights = (next: Record<string, number>) => setUserWeights(next);
  const setCashWeight = (next: number) => setUserCashWeight(next);
  const [collapsed, setCollapsed] = useState(false);

  const reset = () => {
    setUserWeights(null);
    setUserCashWeight(null);
  };

  const equalWeight = () => {
    if (!data) return;
    const n = data.rebalance.perHolding.length;
    const w = 1 / n;
    const next: Record<string, number> = {};
    for (const h of data.rebalance.perHolding) next[h.symbol] = w;
    setWeights(next);
  };

  const stats = useMemo(() => {
    if (!data) return null;
    const order = data.rebalance.covMatrix.symbols;
    const holdingsByOrder: PerHolding[] = order.map(sym => data.rebalance.perHolding.find(h => h.symbol === sym)!).filter(Boolean);
    const w = order.map(sym => weights[sym] ?? 0);
    return computePortfolioStats(holdingsByOrder, w, data.rebalance.covMatrix.matrix, cashWeight, data.fees);
  }, [data, weights, cashWeight]);

  if (!data || !stats) return null;

  const current = data.current;
  const dEr = stats.expectedReturn - current.expectedReturn;
  const dSig = stats.expectedStdev - current.expectedStdev;
  const dSharpe = stats.sharpe - current.expectedSharpe;
  const dComp = stats.composite - current.composite;

  const totalAbs = Object.values(weights).reduce((s, x) => s + x, 0);
  const stockSum = totalAbs;

  const setWeight = (sym: string, pct: number) => {
    setWeights({ ...weights, [sym]: Math.max(0, Math.min(1, pct / 100)) });
  };

  // Sort holdings by current weight for stable display
  const sortedHoldings = [...data.rebalance.perHolding].sort((a, b) => b.currentWeight - a.currentWeight);

  // Detect which preset (if any) the current weight vector matches.
  const portfolioWeights = (() => {
    const out: Record<string, number> = {};
    for (const h of data.rebalance.perHolding) out[h.symbol] = h.currentWeight;
    const sum = Object.values(out).reduce((s, x) => s + x, 0);
    if (sum > 0) for (const k of Object.keys(out)) out[k] = out[k] / sum;
    return out;
  })();
  const equalWeightVal = data.rebalance.perHolding.length > 0 ? 1 / data.rebalance.perHolding.length : 0;
  const matchesPortfolio = data.rebalance.perHolding.every(h => Math.abs((weights[h.symbol] ?? 0) - portfolioWeights[h.symbol]) < 1e-4);
  const matchesEqual = data.rebalance.perHolding.every(h => Math.abs((weights[h.symbol] ?? 0) - equalWeightVal) < 1e-4);
  const mode: 'portfolio' | 'equal' | 'custom' = matchesPortfolio ? 'portfolio' : matchesEqual ? 'equal' : 'custom';

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">Rebalance Simulator</h2>
        <div className="flex items-center gap-2 text-[10px]">
          <div className="inline-flex rounded border border-[#334155] overflow-hidden">
            <button
              type="button"
              onClick={reset}
              title={TIPS.reset}
              aria-pressed={mode === 'portfolio'}
              className={`px-3 py-1 cursor-help transition-colors ${mode === 'portfolio' ? 'bg-amber-400/15 text-amber-200' : 'text-[#94a3b8] hover:bg-[#1e293b]'}`}
            >
              Portfolio weight
            </button>
            <button
              type="button"
              onClick={equalWeight}
              title={TIPS.equalWeight}
              aria-pressed={mode === 'equal'}
              className={`px-3 py-1 border-l border-[#334155] cursor-help transition-colors ${mode === 'equal' ? 'bg-amber-400/15 text-amber-200' : 'text-[#94a3b8] hover:bg-[#1e293b]'}`}
            >
              Equal-weight
            </button>
            {mode === 'custom' && (
              <span className="px-3 py-1 border-l border-[#334155] bg-amber-400/15 text-amber-200">Custom</span>
            )}
          </div>
          <button onClick={() => setCollapsed(!collapsed)} title={TIPS.collapse} className="px-2 py-1 rounded border border-[#334155] text-[#cbd5e1] hover:bg-[#1e293b] cursor-help">
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      </div>

      {/* Plain-English explainer */}
      <div className="bg-[#0a0e14] border border-[#1e293b] rounded-lg px-4 py-3 mb-3 text-[12px] text-[#cbd5e1] leading-relaxed">
        <strong className="text-amber-400">What this does:</strong> drag any slider below — cash or any holding — to see how that trade would change your portfolio in real time. The six numbers up top recompute as you move sliders, and the green/red Δ underneath each tells you whether the move is accretive or dilutive vs your current book. Hover any label (dotted underline) for an explanation. <span className="text-[#64748b]">Read-only — sliders do not save to portfolio.json.</span>
      </div>

      {/* Live stats panel */}
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4 mb-3">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-[12px]">
          <SimStat label="Composite" tip={TIPS.composite} value={stats.composite.toFixed(2)} delta={fmtSigned(dComp)} positive={dComp > 0} />
          <SimStat label="E[r]" tip={TIPS.expectedReturn} value={fmtPct(stats.expectedReturn)} delta={fmtPp(dEr)} positive={dEr > 0} />
          <SimStat label="σ" tip={TIPS.stdev} value={fmtPct(stats.expectedStdev)} delta={fmtPp(dSig)} positive={dSig < 0} />
          <SimStat label="Sharpe (gross)" tip={TIPS.sharpe} value={stats.sharpe.toFixed(2)} delta={fmtSigned(dSharpe)} positive={dSharpe > 0} highlight />
          <SimStat label="β SPY" tip={TIPS.beta} value={stats.beta.toFixed(2)} delta="" positive={false} />
          <SimStat label="Net Sharpe" tip={TIPS.netSharpe} value={stats.netSharpe.toFixed(2)} delta={fmtSigned(stats.netSharpe - current.expectedSharpe + (data.fees.mgmtFee + data.fees.perfFee * Math.max(0, current.expectedReturn)))} positive={false} />
        </div>
        <div className="text-[10px] text-[#64748b] mt-2">
          Cash bucket: {(cashWeight * 100).toFixed(1)}% · Stock weights re-normalized to {((1 - cashWeight) * 100).toFixed(1)}% · sectors HHI {stats.hhi.toFixed(2)}
        </div>
      </div>

      {/* Cash slider */}
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-3 mb-3 flex items-center gap-3">
        <span className="text-[11px] text-[#cbd5e1] w-24">
          <Tip text={TIPS.cashSlider}>Cash</Tip>
        </span>
        <input
          type="range" min={0} max={100} step={0.5}
          value={(cashWeight * 100).toFixed(1)}
          onChange={(e) => setCashWeight(Math.max(0, Math.min(1, Number(e.target.value) / 100)))}
          className="flex-1 accent-amber-400"
        />
        <span className="text-[12px] tabular-nums text-amber-400 font-bold w-16 text-right">{(cashWeight * 100).toFixed(1)}%</span>
      </div>

      {!collapsed && (
        <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
          {/* Column headers */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[9px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]">
            <div className="col-span-2"><Tip text={TIPS.rowSymbol}>Symbol</Tip></div>
            <div className="col-span-2 text-left">Sector · score</div>
            <div className="col-span-4"><Tip text={TIPS.rowSlider}>Simulated weight</Tip></div>
            <div className="col-span-2 text-right"><Tip text={TIPS.rowCurrent}>Current</Tip></div>
            <div className="col-span-2 text-right"><Tip text={TIPS.rowDelta}>Δ vs current</Tip></div>
          </div>

          <div className="px-2 py-1">
            {sortedHoldings.map((h) => {
              const w = weights[h.symbol] ?? 0;
              const renorm = stockSum > 0 ? (w / stockSum) * (1 - cashWeight) : 0;
              const currentW = h.currentWeight;
              const dW = renorm - currentW;
              // Color buckets: high(≥3) / moderate(2-3) / low(1-2) / very low(<1) / sell (<0).
              const compTier = h.composite >= 3 ? 'text-emerald-400' : h.composite >= 2 ? 'text-amber-300' : h.composite >= 1 ? 'text-amber-500/80' : h.composite >= 0 ? 'text-[#94a3b8]' : 'text-red-400';
              return (
                <div key={h.symbol} className="grid grid-cols-12 gap-2 items-center text-[11px] px-2 py-0.5 rounded hover:bg-[#1e293b]/30">
                  <div className="col-span-2 font-mono font-bold text-white text-[12px]">{h.symbol}</div>
                  <div className="col-span-2 text-[10px] text-[#94a3b8] truncate">
                    {h.sector} · <span className={`font-semibold ${compTier}`}>{h.composite.toFixed(2)}</span>
                  </div>
                  <div className="col-span-4 flex items-center gap-2">
                    <input
                      type="range" min={0} max={50} step={0.1}
                      value={(w * 100).toFixed(1)}
                      onChange={(e) => setWeight(h.symbol, Number(e.target.value))}
                      className="flex-1 accent-amber-400"
                    />
                    <input
                      type="number" min={0} max={100} step={0.1}
                      value={(w * 100).toFixed(1)}
                      onChange={(e) => setWeight(h.symbol, Number(e.target.value))}
                      className="w-14 bg-[#0a0e14] border border-[#1e293b] rounded px-1 py-0.5 text-[#e2e8f0] text-right text-[11px] tabular-nums focus:outline-none focus:border-amber-500/50"
                    />
                  </div>
                  <div className="col-span-2 text-[10px] text-[#94a3b8] text-right tabular-nums">
                    {fmtPct(currentW)}
                  </div>
                  <div className={`col-span-2 text-[11px] text-right tabular-nums font-semibold ${Math.abs(dW) < 0.0005 ? 'text-[#64748b]' : dW > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {Math.abs(dW) < 0.0005 ? '—' : fmtPp(dW)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-[10px] text-[#64748b] mt-2 leading-relaxed">
        <strong className="text-[#cbd5e1]">How weights work:</strong> sliders set raw values. They auto-renormalize so the stocks bucket sums to <em>(100% − cash)</em>. So if you bump one slider up, every other name proportionally shrinks to make room. The <strong>Δ vs current</strong> column on the right shows the final renormalized change. <span className="text-emerald-400">Green</span> = added exposure. <span className="text-red-400">Red</span> = trimmed.
      </div>
    </section>
  );
}

function SimStat({ label, value, delta, positive, highlight = false, tip }: { label: string; value: string; delta: string; positive: boolean; highlight?: boolean; tip?: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">
        {tip ? <Tip text={tip}>{label}</Tip> : label}
      </div>
      <div className={`text-[18px] font-bold tabular-nums ${highlight ? 'text-amber-400' : 'text-white'}`}>{value}</div>
      {delta && (
        <div className={`text-[10px] tabular-nums ${positive ? 'text-emerald-400' : 'text-red-400'}`}>{delta} vs current</div>
      )}
    </div>
  );
}
